// frontend/src/pages/UsoPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import contentService from '../services/contentService';

import Loading from '../components/Loading';
/* ── Helpers ─────────────────────────────────────────────────────────── */
function initials(name) {
  if (!name) return '?';
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function avatarColor(name) {
  const palette = [
    '#1877F2', '#833AB4', '#E1306C', '#0D47A1',
    '#167A52', '#D4500A', '#00838F', '#7B3FAD',
  ];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
}

/* ── Component ───────────────────────────────────────────────────────── */
export default function UsoPage() {
  const [actividad, setActividad]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchActivity = useCallback(() => {
    contentService.getUserActivity()
      .then(res => {
        setActividad(res.data.actividad || []);
        setLastUpdate(new Date());
        setError(null);
      })
      .catch(err => {
        if (err.response?.status === 403) {
          setError('No tienes permisos para ver esta pantalla.');
        } else {
          setError('Error al cargar los datos.');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Carga inicial
  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  // Auto-refresh cada 30 segundos
  useEffect(() => {
    const timer = setInterval(fetchActivity, 30_000);
    return () => clearInterval(timer);
  }, [fetchActivity]);

  const activos   = actividad.filter(u => u.activo);
  const inactivos = actividad.filter(u => !u.activo);

  /* ── Render ── */
  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Usuarios en plataforma</h1>
          <p style={S.subtitle}>
            Sesiones activas y última pantalla visitada por cada usuario.
          </p>
        </div>
        <div style={S.headerRight}>
          {lastUpdate && (
            <span style={S.updateLabel}>
              Actualizado hace {Math.round((Date.now() - lastUpdate) / 1000)}s
            </span>
          )}
          <button style={S.btnRefresh} onClick={fetchActivity}>
            ↻ Actualizar
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={S.errorBox}>{error}</div>
      )}

      {/* Loading */}
      {loading && !error && (
        <Loading fullScreen={false} mensaje="Cargando actividad" />
      )}

      {/* Stats */}
      {!loading && !error && (
        <>
          <div style={S.statsRow}>
            <div style={{ ...S.statCard, borderTop: '3px solid #22c55e' }}>
              <div style={{ ...S.statValue, color: '#22c55e' }}>{activos.length}</div>
              <div style={S.statLabel}>Activos ahora</div>
            </div>
            <div style={{ ...S.statCard, borderTop: '3px solid #94a3b8' }}>
              <div style={{ ...S.statValue, color: '#64748b' }}>{inactivos.length}</div>
              <div style={S.statLabel}>Inactivos</div>
            </div>
            <div style={{ ...S.statCard, borderTop: '3px solid #1877F2' }}>
              <div style={{ ...S.statValue, color: '#1877F2' }}>{actividad.length}</div>
              <div style={S.statLabel}>Total registrados</div>
            </div>
          </div>

          {/* Tabla */}
          <div style={S.card}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Estado</th>
                  <th style={S.th}>Usuario</th>
                  <th style={S.th}>Última pantalla</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Hace</th>
                </tr>
              </thead>
              <tbody>
                {actividad.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '40px 0', color: '#aaa', fontSize: 14 }}>
                      Sin actividad registrada todavía.
                    </td>
                  </tr>
                )}
                {actividad.map((u, i) => (
                  <tr key={u.user} style={i % 2 === 0 ? S.trEven : {}}>
                    {/* Estado */}
                    <td style={S.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: u.activo ? '#22c55e' : '#cbd5e1',
                          flexShrink: 0,
                          boxShadow: u.activo ? '0 0 0 3px rgba(34,197,94,.2)' : 'none',
                        }} />
                        <span style={{
                          fontSize: 12, fontWeight: 600,
                          color: u.activo ? '#16a34a' : '#94a3b8',
                        }}>
                          {u.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </td>

                    {/* Usuario */}
                    <td style={S.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          ...S.avatar,
                          background: avatarColor(u.username),
                        }}>
                          {initials(u.username)}
                        </div>
                        <div>
                          <div style={S.userName}>{u.username}</div>
                        </div>
                      </div>
                    </td>

                    {/* Módulo */}
                    <td style={S.td}>
                      <span style={{
                        ...S.moduleBadge,
                        background: u.activo ? 'rgba(113,210,52,0.08)' : 'var(--g-fondo)',
                        color: u.activo ? '#166534' : '#475569',
                        border: `1px solid ${u.activo ? '#bbf7d0' : '#e2e8f0'}`,
                      }}>
                        {u.modulo}
                      </span>
                    </td>

                    {/* Hace */}
                    <td style={{ ...S.td, textAlign: 'right', color: '#94a3b8', fontSize: 13, fontWeight: 500 }}>
                      {u.hace}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p style={{ fontSize: 11, color: '#bbb', marginTop: 10, textAlign: 'right' }}>
            Se considera "Activo" si se registró actividad en los últimos 5 minutos. Refresco automático cada 30s.
          </p>
        </>
      )}
    </div>
  );
}

/* ── Styles ──────────────────────────────────────────────────────────── */
const S = {
  page:        { padding: '28px 32px', maxWidth: 960, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', color: '#1a1a1a' },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 12, flexWrap: 'wrap' },
  title:       { margin: 0, fontSize: 22, fontWeight: 700 },
  subtitle:    { margin: '4px 0 0', color: '#666', fontSize: 13 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 },
  updateLabel: { fontSize: 12, color: '#94a3b8' },
  btnRefresh:  { padding: '7px 16px', background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  errorBox:    { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px 16px', borderRadius: 8, fontSize: 14, marginBottom: 16 },
  loading:     { textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 14 },
  statsRow:    { display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' },
  statCard:    { flex: '1 1 140px', background: '#fff', border: '1px solid var(--g-border)', borderRadius: 'var(--g-radius-lg)', padding: '14px 18px', boxShadow: 'var(--g-shadow-sm)' },
  statValue:   { fontSize: 28, fontWeight: 800, lineHeight: 1.1 },
  statLabel:   { fontSize: 11, color: 'var(--g-text-muted)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '.4px' },
  card:        { background: '#fff', border: '1px solid var(--g-border)', borderRadius: 'var(--g-radius-lg)', boxShadow: 'var(--g-shadow-sm)', overflow: 'hidden' },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--g-text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: '1px solid var(--g-border)', background: 'var(--g-fondo)' },
  td:          { padding: '13px 16px', fontSize: 14, verticalAlign: 'middle', borderBottom: '1px solid var(--g-border)' },
  trEven:      { background: 'var(--g-fondo)' },
  avatar:      { width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 12, flexShrink: 0 },
  userName:    { fontWeight: 700, fontSize: 14, color: 'var(--g-dark)' },
  moduleBadge: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
};
